import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Phone, Mail, DoorOpen, Maximize2, Shield, Home } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import SchemaMarkup from "@/components/SchemaMarkup";
import FAQ from "@/components/FAQ";
import OptimizedImage from "@/components/OptimizedImage";
import heroImage from "@/assets/windows-doors-hero.jpg";
import windowInstallation from "@/assets/window-installation-ontario.jpg";
import doorInstallation from "@/assets/door-installation-ontario.jpg";
import commercialGlass from "@/assets/commercial-glass-installation.jpg";
import patioDoor from "@/assets/patio-door-installation.jpg";

const WindowsDoorsServices = () => {
  const faqData = [
    {
      question: "What types of windows do you install in the Niagara Region?",
      answer: "We install a comprehensive range of energy-efficient windows including double-hung, casement, awning, sliding, bay and bow windows, picture windows, and custom specialty windows. All our windows meet or exceed Ontario Building Code requirements and are ENERGY STAR certified for maximum energy efficiency."
    },
    {
      question: "How long does a typical door installation take in Hamilton?",
      answer: "Most residential door installations can be completed in 4-8 hours depending on the complexity. Entry door replacements typically take one day, while patio door installations may require 1-2 days. Commercial door installations vary based on size and specifications. We provide detailed timelines during your consultation."
    },
    {
      question: "Do you offer warranty coverage for windows and doors in Toronto GTA?",
      answer: "Yes, we provide comprehensive warranty coverage including manufacturer warranties (typically 10-20 years on windows, 5-10 years on doors) plus our workmanship guarantee. We also offer extended warranty options for additional peace of mind. All warranties are fully transferable if you sell your property."
    },
    {
      question: "Are your windows and doors energy efficient for Ontario's climate?",
      answer: "Absolutely. All our windows and doors are specifically selected for Ontario's climate with low-E glass, argon gas fills, and insulated frames. Our products help reduce heating costs by up to 25%, minimize condensation, and provide excellent noise reduction. They meet or exceed ENERGY STAR standards for Canada's climate zones."
    },
    {
      question: "Can you match new windows and doors to my existing home style?",
      answer: "Yes, we specialize in matching architectural styles and can custom order windows and doors in various materials (vinyl, wood, aluminum, fiberglass), colors, finishes, and designs to perfectly complement your home's aesthetic. We work with leading manufacturers to ensure perfect style coordination."
    },
    {
      question: "Do you provide window and door installation for commercial properties?",
      answer: "Yes, we offer comprehensive commercial window and door solutions including storefront systems, automatic doors, commercial-grade entry systems, security doors, and large-scale window installations. We work with property managers, business owners, and commercial contractors throughout Ontario."
    }
  ];

  const services = [
    {
      icon: Maximize2,
      title: "Window Installation & Replacement",
      description: "Energy-efficient vinyl, wood, and fiberglass windows",
      features: [
        "Double-hung and casement windows",
        "Bay and bow window installations",
        "Sliding and awning windows",
        "Picture and specialty windows",
        "Energy Star certified products",
        "Custom sizes and configurations"
      ]
    },
    {
      icon: DoorOpen,
      title: "Entry Door Installation",
      description: "High-quality residential and commercial entry doors",
      features: [
        "Steel and fiberglass entry doors",
        "Wood and composite doors",
        "Custom door designs",
        "Smart lock integration",
        "Weather-resistant sealing",
        "Security-enhanced options"
      ]
    },
    {
      icon: Home,
      title: "Patio & Sliding Doors",
      description: "Premium patio door systems for seamless indoor-outdoor living",
      features: [
        "Sliding patio door installation",
        "French door systems",
        "Multi-slide and bi-fold doors",
        "Energy-efficient glass options",
        "Low-threshold accessibility",
        "Custom sizes available"
      ]
    },
    {
      icon: Shield,
      title: "Commercial Glass & Storefronts",
      description: "Professional commercial glazing solutions",
      features: [
        "Storefront window systems",
        "Automatic door installation",
        "Security glass options",
        "Commercial entry systems",
        "Curtain wall installation",
        "Tempered and laminated glass"
      ]
    }
  ];

  return (
    <>
      <SEOMeta
        title="Windows & Doors — Install & Replacement | Niagara • Hamilton • GTA"
        description="Energy-efficient windows and door installations across Niagara, Hamilton & the GTA. Improve comfort, curb appeal and energy savings."
        keywords="window contractor Niagara, Hamilton door installer, GTA window replacement, entry doors, patio doors, casement windows, picture windows"
        canonicalUrl="https://blueknightcontracting.ca/services/windows-doors"
      />
      <SchemaMarkup
        type="Service"
        data={{
          serviceType: "Windows and Doors Installation",
          serviceName: "Professional Windows & Doors Services in Ontario",
          services: [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Window Installation Niagara Region",
                "description": "Professional installation of energy-efficient windows including double-hung, casement, bay, and specialty windows in Niagara Region."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Door Installation Hamilton",
                "description": "Complete door installation services including entry doors, patio doors, and commercial door systems in Hamilton and surrounding areas."
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Commercial Glass Installation Toronto GTA",
                "description": "Commercial glazing services including storefront systems, automatic doors, and security glass installation throughout Toronto GTA."
              }
            }
          ]
        }}
      />
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
          <OptimizedImage
            src={heroImage}
            alt="Professional windows and doors installation services in Niagara Region, Hamilton, and Toronto GTA - Blue Knight Contracting"
            className="absolute inset-0 w-full h-full object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/70 to-background/50" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                Windows & Doors Installation Services
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-muted-foreground">
                Energy-efficient windows and doors for homes and businesses across Niagara Region, Hamilton, and Toronto GTA
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" asChild className="text-lg">
                  <a href="/contact">
                    Get Free Estimate <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild className="text-lg">
                  <a href="tel:4165552583">
                    <Phone className="mr-2 h-5 w-5" /> (416) 555-2583
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Comprehensive Windows & Doors Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From residential window replacements to commercial storefront installations, we deliver quality products and expert installation
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="bg-primary/10 p-3 rounded-lg">
                          <Icon className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                          <p className="text-muted-foreground">{service.description}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <Check className="h-5 w-5 text-primary flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Residential Windows Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <OptimizedImage
                  src={windowInstallation}
                  alt="Professional window installation in modern Ontario home - energy-efficient double-pane windows"
                  className="rounded-lg shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Residential Window Installation
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Transform your home with energy-efficient windows that reduce heating costs, minimize noise, and enhance curb appeal. Our expert installers ensure perfect fit and proper sealing for maximum performance.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Energy Star Certified Products</h3>
                      <p className="text-muted-foreground">Save up to 25% on heating and cooling costs with our high-performance windows</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Lifetime Warranty Coverage</h3>
                      <p className="text-muted-foreground">Manufacturer warranties plus our installation guarantee for complete peace of mind</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Custom Sizes & Styles</h3>
                      <p className="text-muted-foreground">Perfect match for any architectural style in Niagara, Hamilton, or Toronto</p>
                    </div>
                  </div>
                </div>
                <Button size="lg" asChild>
                  <a href="/contact">Schedule Consultation</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Entry Doors Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Entry Door Installation & Replacement
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Make a lasting first impression with a beautiful, secure entry door. We install high-quality steel, fiberglass, and wood doors with advanced security features and weather-resistant sealing.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Enhanced Security Features</h3>
                      <p className="text-muted-foreground">Multi-point locking systems and reinforced frames for maximum protection</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Weather-Tight Sealing</h3>
                      <p className="text-muted-foreground">Superior insulation to withstand Ontario's harsh winters and hot summers</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Smart Lock Ready</h3>
                      <p className="text-muted-foreground">Compatible with modern smart lock systems for keyless convenience</p>
                    </div>
                  </div>
                </div>
                <Button size="lg" asChild>
                  <a href="/contact">Explore Door Options</a>
                </Button>
              </div>
              <div className="order-1 lg:order-2">
                <OptimizedImage
                  src={doorInstallation}
                  alt="Modern entry door installation in Toronto GTA home - contemporary design with glass panels"
                  className="rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Patio Doors Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <OptimizedImage
                  src={patioDoor}
                  alt="Elegant patio door installation in Niagara Region - sliding glass door with scenic backyard view"
                  className="rounded-lg shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Patio & Sliding Door Systems
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Connect your indoor and outdoor living spaces with beautiful, functional patio doors. Our sliding, French, and multi-slide door systems offer smooth operation, energy efficiency, and stunning views.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Effortless Operation</h3>
                      <p className="text-muted-foreground">Smooth gliding systems with advanced rollers for years of trouble-free use</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Maximum Natural Light</h3>
                      <p className="text-muted-foreground">Large glass panels to brighten your space and showcase outdoor views</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Accessible Design Options</h3>
                      <p className="text-muted-foreground">Low-threshold and barrier-free configurations available</p>
                    </div>
                  </div>
                </div>
                <Button size="lg" asChild>
                  <a href="/contact">View Patio Door Options</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial Glass Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Commercial Glass & Storefront Installation
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Professional commercial glazing services for retail, office, and industrial properties. We install storefront systems, automatic doors, and custom glass solutions that enhance your business image and functionality.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Aluminum Storefront Systems</h3>
                      <p className="text-muted-foreground">Durable, attractive entrance systems that welcome customers and withstand heavy use</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Automatic Door Integration</h3>
                      <p className="text-muted-foreground">ADA-compliant automatic door systems for accessibility and convenience</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Security Glass Options</h3>
                      <p className="text-muted-foreground">Tempered, laminated, and bullet-resistant glass for enhanced security</p>
                    </div>
                  </div>
                </div>
                <Button size="lg" asChild>
                  <a href="/contact">Get Commercial Quote</a>
                </Button>
              </div>
              <div className="order-1 lg:order-2">
                <OptimizedImage
                  src={commercialGlass}
                  alt="Commercial storefront glass installation in Hamilton - professional glaziers installing modern glass entrance"
                  className="rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Why Choose Blue Knight for Windows & Doors?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center">
                <CardHeader>
                  <CardTitle className="flex flex-col items-center gap-3">
                    <div className="bg-primary/10 p-4 rounded-full">
                      <Check className="h-8 w-8 text-primary" />
                    </div>
                    Licensed & Insured Installers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    All our technicians are fully licensed, insured, and factory-trained for proper installation techniques and safety protocols.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <CardTitle className="flex flex-col items-center gap-3">
                    <div className="bg-primary/10 p-4 rounded-full">
                      <Shield className="h-8 w-8 text-primary" />
                    </div>
                    Comprehensive Warranties
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Industry-leading manufacturer warranties plus our workmanship guarantee ensure your investment is protected for years to come.
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardHeader>
                  <CardTitle className="flex flex-col items-center gap-3">
                    <div className="bg-primary/10 p-4 rounded-full">
                      <Home className="h-8 w-8 text-primary" />
                    </div>
                    Local Expertise
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Serving Niagara Region, Hamilton, and Toronto GTA for over 20 years with products designed for Ontario's climate.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <FAQ
              title="Frequently Asked Questions"
              description="Get answers to common questions about our windows and doors installation services"
              faqs={faqData}
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Upgrade Your Windows & Doors?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
              Contact us today for a free consultation and estimate. Serving Niagara Region, Hamilton, and Toronto GTA.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" variant="secondary" asChild>
                <a href="/contact">
                  <Mail className="mr-2 h-5 w-5" /> Request Free Estimate
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <a href="tel:4165552583">
                  <Phone className="mr-2 h-5 w-5" /> (416) 555-2583
                </a>
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default WindowsDoorsServices;
